/**
 * BNPL data screen logic (ERS-5, screen 08 since ERS-78)
 *
 * State, validation, the `privateInfo` write and the email opt-in trigger for the BNPL data screen;
 * the screen component only renders. Consents and the provider submit live in
 * `hooks/useBnplPaymentScreen.ts` (09). It writes `billingType` only for a minor, whose route ends at
 * install, and suppresses its explain guard before that write.
 *
 * References:
 * - `insurance/bnplBillingCustomer.ts` — `buildBillingPhone`, `readBirthdayIso`
 * - `utils/streetAddress.ts` — "Straße und Hausnummer" splitting
 */

import type { SignedUpPatient } from '@physiofit-digital/physiofit_types';
import { PATIENT_TRACKING_EVENTS } from '@physiofit-digital/physiofit_types';
import { usePostHog } from '@posthog/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { sendOnboardingEmailVerification } from '../api/onboardingEmailCallables';
import {
  buildBillingPhone,
  isMinorBirthday,
  isPlausibleBirthday,
  readBirthdayIso,
} from '../insurance/bnplBillingCustomer';
import { findDisallowedBnplCharacters, findDisallowedBnplEmailCharacters } from '../insurance/bnplAllowedCharacters';
import {
  BNPL_BIRTHDAY_INVALID_ERROR,
  BNPL_CHECKOUT_GENERIC_ERROR_NOTICE,
  buildBnplDisallowedCharactersError,
} from '../insurance/bnplCopy';
import { MINOR_BLOCKED_REASON } from '../insurance/config';
import { getBnplPaymentPath, getInsuranceExplainPath } from '../insurance/routing';
import { editSignedUpPatient } from '../services/editSignedUpPatient';
import { useSaveOnboardingStep } from '../services/onboardingProgress';
import {
  getSignedUpPatient,
  getSignUpId,
  getVerifiedPatientNamesFromAuth,
  useOnboardingStore,
} from '../store/onboardingStore';
import { joinStreet, splitStreet } from '../utils/streetAddress';
import { useInsuranceExplainBillingGuard } from './useInsuranceExplainBillingGuard';
import { useSelectedInsurance } from './useSelectedInsurance';

export type BnplCheckoutFormState = {
  firstName: string;
  lastName: string;
  street: string;
  zip: string;
  city: string;
  /** `YYYY-MM-DD` as typed (not yet validated), or `''` while a birthday box is empty. */
  birthday: string;
  email: string;
};

const EMPTY_FORM: BnplCheckoutFormState = {
  firstName: '',
  lastName: '',
  street: '',
  zip: '',
  city: '',
  birthday: '',
  email: '',
};

const CHARACTER_CHECKED_FIELDS = ['firstName', 'lastName', 'street', 'zip', 'city', 'email'] as const;
type CharacterCheckedField = (typeof CHARACTER_CHECKED_FIELDS)[number];

function isValidEmail(email: string): boolean {
  return /\S+@\S+\.\S+/.test(email.trim());
}

/**
 * First value that survives trimming. `??` is not enough here: a field the practice cleared is
 * stored as `''`, not absent, and would otherwise win over a usable fallback.
 */
function firstFilled(...values: Array<string | undefined>): string {
  return values.find((value) => value?.trim())?.trim() ?? '';
}

/**
 * Prefills the checkout form from everything the invitation already knows.
 *
 * Two sources, in that order of precedence: what the patient typed in this flow
 * (`webAppOnboarding.privateInfo`) always wins, then the invitation's own master data — which the
 * practice maintains and which PF-4935 keeps in step with `practicePatients`. The two use different
 * field names for the same values (`streetName`/`zipCode` vs `street`/`zip`), which is why this
 * cannot be a spread.
 *
 * @param {Object} patient Signed-up patient document.
 * @param {Object | undefined} verifiedNames Names confirmed during phone auth.
 * @return {Object} Initial form state.
 */
function deriveFormFromPatient(
  patient: SignedUpPatient,
  verifiedNames: { firstName: string; lastName: string } | undefined,
): BnplCheckoutFormState {
  const privateInfo = patient.webAppOnboarding?.privateInfo;
  const flowAddress = privateInfo?.address;
  const masterAddress = patient.address;

  return {
    firstName: firstFilled(privateInfo?.firstName, verifiedNames?.firstName, patient.patientFirstName),
    lastName: firstFilled(privateInfo?.lastName, verifiedNames?.lastName, patient.patientLastName),
    street: firstFilled(
      joinStreet({ street: flowAddress?.street, houseNumber: flowAddress?.houseNumber }),
      joinStreet({ street: masterAddress?.streetName, houseNumber: masterAddress?.houseNumber }),
    ),
    zip: firstFilled(flowAddress?.zip, masterAddress?.zipCode),
    city: firstFilled(flowAddress?.city, masterAddress?.city),
    // `readBirthdayIso` drops anything unparseable, so a malformed stored value falls through to the
    // next source instead of poisoning the birthday boxes.
    birthday: firstFilled(readBirthdayIso(privateInfo?.birthday), readBirthdayIso(patient.patientBirthday)),
    email: firstFilled(privateInfo?.email, patient.patientEmail),
  };
}

export function useBnplCheckoutScreen() {
  useSaveOnboardingStep('insurance_bnpl_checkout');
  // Set before a minor's `billingType` write and never cleared: the only way off this screen after it is install.
  const exitHandledRef = useRef(false);
  useInsuranceExplainBillingGuard('bnpl', { suppressedRef: exitHandledRef });
  const posthog = usePostHog();
  const navigate = useNavigate();
  const signedUpPatient = useOnboardingStore(getSignedUpPatient);
  const signUpId = useOnboardingStore(getSignUpId);
  const verifiedNames = useOnboardingStore(getVerifiedPatientNamesFromAuth);
  const { insurance, loading: insuranceLoading, error: insuranceError } = useSelectedInsurance();

  const formInitializedRef = useRef(false);
  const [form, setForm] = useState<BnplCheckoutFormState>(EMPTY_FORM);
  useEffect(() => {
    if (formInitializedRef.current || !signedUpPatient) {
      return;
    }
    formInitializedRef.current = true;
    setForm(deriveFormFromPatient(signedUpPatient, verifiedNames));
  }, [signedUpPatient, verifiedNames]);

  const [fieldsError, setFieldsError] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const fieldsRef = useRef<HTMLDivElement>(null);

  const phoneDisplay = useMemo(() => {
    if (!signedUpPatient) {
      return '';
    }
    return buildBillingPhone(signedUpPatient);
  }, [signedUpPatient]);

  const requiredFieldsOk = useMemo(() => {
    return (
      Boolean(form.firstName.trim()) &&
      Boolean(form.lastName.trim()) &&
      Boolean(form.street.trim()) &&
      Boolean(form.zip.trim()) &&
      Boolean(form.city.trim()) &&
      Boolean(form.birthday.trim()) &&
      isValidEmail(form.email)
    );
  }, [form]);

  // Kept out of `requiredFieldsOk`: its failure shows "Bitte fülle alle Felder aus", which is wrong for a character problem.
  const charErrors = useMemo(() => {
    const errors: Partial<Record<CharacterCheckedField, string>> = {};
    for (const key of CHARACTER_CHECKED_FIELDS) {
      const findDisallowed = key === 'email' ? findDisallowedBnplEmailCharacters : findDisallowedBnplCharacters;
      const characters = findDisallowed(form[key]);
      if (characters.length > 0) {
        errors[key] = buildBnplDisallowedCharactersError(characters);
      }
    }
    return errors;
  }, [form]);
  const hasCharErrors = Object.keys(charErrors).length > 0;

  // Kept out of `requiredFieldsOk` like `charErrors`: a typed but impossible date is not a missing field.
  const birthdayError = form.birthday && !isPlausibleBirthday(form.birthday) ? BNPL_BIRTHDAY_INVALID_ERROR : undefined;

  const setField = useCallback(<K extends keyof BnplCheckoutFormState>(key: K, value: BnplCheckoutFormState[K]) => {
    setForm((current) => ({ ...current, [key]: value }));
  }, []);

  const handleBack = useCallback(() => navigate(getInsuranceExplainPath('bnpl')), [navigate]);

  const handleForward = useCallback(async () => {
    if (!requiredFieldsOk) {
      setFieldsError(true);
      fieldsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    if (hasCharErrors || birthdayError) {
      fieldsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    if (!signUpId) return;
    setIsSaving(true);
    setSaveError(null);
    try {
      const { street, houseNumber } = splitStreet(form.street);
      await editSignedUpPatient({
        'webAppOnboarding.privateInfo.firstName': form.firstName.trim(),
        'webAppOnboarding.privateInfo.lastName': form.lastName.trim(),
        'webAppOnboarding.privateInfo.email': form.email.trim(),
        'webAppOnboarding.privateInfo.birthday': form.birthday,
        'webAppOnboarding.privateInfo.address.street': street,
        'webAppOnboarding.privateInfo.address.houseNumber': houseNumber,
        'webAppOnboarding.privateInfo.address.zip': form.zip.trim(),
        'webAppOnboarding.privateInfo.address.city': form.city.trim(),
      });
      // Bookkeeping after the durable write: a mail failure must never cost the patient the checkout.
      try {
        await sendOnboardingEmailVerification({ signedUpPatientId: signUpId });
      } catch (error) {
        console.error('sendOnboardingEmailVerification failed', error);
      }
      if (isMinorBirthday(form.birthday)) {
        // The order overview (09) holds the contract; a minor's course cannot be billed to the insurer.
        exitHandledRef.current = true;
        await editSignedUpPatient({
          'webAppOnboarding.insuranceInfo.billingType': 'blocked',
          'webAppOnboarding.insuranceInfo.blockedReason': MINOR_BLOCKED_REASON,
        });
        const patient = useOnboardingStore.getState().signedUpPatient;
        const insuranceInfo = patient?.webAppOnboarding?.insuranceInfo;
        if (patient?.webAppOnboarding && insuranceInfo) {
          useOnboardingStore.getState().setSignedUpPatient({
            ...patient,
            webAppOnboarding: {
              ...patient.webAppOnboarding,
              insuranceInfo: { ...insuranceInfo, billingType: 'blocked', blockedReason: MINOR_BLOCKED_REASON },
            },
          });
        }
        posthog.capture(PATIENT_TRACKING_EVENTS.web_insurance_bnpl_minor_redirected, { insuranceId: insurance?.id });
        navigate('/webAppOnboarding/install');
        return;
      }
      navigate(getBnplPaymentPath());
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : BNPL_CHECKOUT_GENERIC_ERROR_NOTICE);
      setIsSaving(false);
    }
  }, [requiredFieldsOk, hasCharErrors, birthdayError, form, navigate, signUpId, posthog, insurance]);

  return {
    insurance,
    insuranceLoading,
    insuranceError,
    phoneDisplay,
    form,
    charErrors,
    birthdayError,
    setField,
    forwardDisabled: insuranceLoading || isSaving,
    forwardInactive: !requiredFieldsOk || hasCharErrors || Boolean(birthdayError),
    forwardBusy: isSaving,
    fieldsError: fieldsError && !requiredFieldsOk,
    saveError,
    fieldsRef,
    handleBack,
    handleForward,
  };
}

export type UseBnplCheckoutScreenResult = ReturnType<typeof useBnplCheckoutScreen>;
